// ABOUTME: Lists the events users noted on an order, with date, time and user per event.
// ABOUTME: Long messages are cut off on one line; hovering an event shows the full message. Events can be edited and deleted.
import { useState } from "react";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { tokens } from "../../../../../theme";
import Subheading from "../../../../theme/text/Subheading";
import Text from "../../../../theme/text/Text";
import { ENV_TYPES } from "../../../Sidebar";
import { useGetOrderEventsQuery, useUpdateOrderEventMutation, useDeleteOrderEventMutation } from "../../../../../newapi/order/ordersSlice";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import { showToastMessageDeleteRequest, showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../../../utils/toasts";
import MDynamicValues from "../../../Modals/MDynamicValues";
import MConfirmation from "../../../Modals/MConfirmation";

export const OrderEvents = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    // Events belong to the relation portal and the own-stock version; a fulfilment warehouse does not see them.
    const canSeeEvents = isRelation || !isFulfilment;

    const { data: events } = useGetOrderEventsQuery(props.orderId, {
        skip: !props.orderId || !canSeeEvents
    });

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.OUTBOUND_MANAGE,UserPrivileges.RP_ORDER_MANAGE])
    );

    const [eventToEdit, setEventToEdit] = useState(null);
    const [eventToDelete, setEventToDelete] = useState(null);

    const [updateOrderEvent,{isLoading: isUpdateLoading}] = useUpdateOrderEventMutation();
    const handleUpdateEvent = async (values) => {
        try {
            if (isUpdateLoading) return;

            const data = await updateOrderEvent({
                id: props.orderId,
                eventId: eventToEdit.id,
                message: values.message,
            }).unwrap()
            showToastMessageUpdateRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    }

    const [deleteOrderEvent,{isLoading: isDeleteLoading}] = useDeleteOrderEventMutation();
    const handleDeleteEvent = async () => {
        try {
            if (isDeleteLoading) return;

            const data = await deleteOrderEvent({
                id: props.orderId,
                eventId: eventToDelete.id,
            }).unwrap()
            showToastMessageDeleteRequest(t, data);
        } catch (err) {
            showToastMessageRequestError(t("delete_failed"), t, err);
        }
    }

    if (!canSeeEvents || !events || events.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                px:3,
                ...props.sx,
            }}
        >
            <Subheading
                sx={{
                    pb:1.5,
                }}
            >
                {t("events")}
            </Subheading>
            <Box
                sx={{
                    display:"flex",
                    flexDirection:'column',
                    gap:1.5,
                }}
            >
                {events.map((event) => (
                    <Tooltip
                        key={event.id}
                        title={<Box sx={{ whiteSpace:"pre-wrap" }}>{event.message}</Box>}
                        placement="top-start"
                    >
                        <Box
                            sx={{
                                borderRadius:3,
                                px:2,
                                py:1.5,
                                minWidth:0,
                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                            }}
                        >
                            <Box
                                sx={{
                                    display:"flex",
                                    justifyContent:"space-between",
                                    gap:2,
                                }}
                            >
                                <Text semibold>{event.userName || t("fulfilment")}</Text>
                                <Box sx={{ flexShrink:0, display:"flex", alignItems:"center", gap:0.5 }}>
                                    <Box>
                                        <Text>{new Date(event.createdAt).toLocaleDateString(i18n.language)}</Text>&nbsp;
                                        <Text light>{new Date(event.createdAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</Text>
                                    </Box>
                                    {hasModifyPrivilege &&
                                        <>
                                            <IconButton size="small" aria-label={t("edit_event")} onClick={() => setEventToEdit(event)} sx={{ color:colors.txt["secondary"] }}>
                                                <EditOutlinedIcon fontSize="small"/>
                                            </IconButton>
                                            <IconButton size="small" aria-label={t("delete_event")} onClick={() => setEventToDelete(event)} sx={{ color:colors.txt["secondary"] }}>
                                                <DeleteOutlineOutlinedIcon fontSize="small"/>
                                            </IconButton>
                                        </>
                                    }
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    overflow:"hidden",
                                    textOverflow:"ellipsis",
                                    whiteSpace:"nowrap",
                                }}
                            >
                                <Text light>{event.message}</Text>
                            </Box>
                        </Box>
                    </Tooltip>
                ))}
            </Box>
            <MDynamicValues
                open={eventToEdit !== null}
                handleClose={() => setEventToEdit(null)}
                title={t("edit_event")}
                subtitle={isRelation ? t("event_internal_subtitle") : null}
                labelWidth={120}
                fields={[
                    { name: "message", label: t("description"), type: "text", required: true, defaultValue: eventToEdit?.message ?? "" },
                ]}
                onConfirm={(values) => handleUpdateEvent(values)}
            />
            <MConfirmation
                open={eventToDelete !== null}
                handleClose={() => setEventToDelete(null)}
                title={t("delete_event")}
                subtitle={t("mc_delete_event_subtitle")}
                confirm={() => handleDeleteEvent()}
            />
        </Box>
    );
};
